import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendOtp } from '../../services/authApi';
import { pingHealth } from '../../services/healthApi';
import { resolveMirror } from '../../services/mirrorApi';
import { API_BASE_URL } from '../../config/constants';
import AppVersion from '../../components/AppVersion';
import './MobileLogin.css';
import BrandLogo from '../../components/BrandLogo';

const MobileLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Mirror code from QR can be mirrorCode (preferred) or internal mirrorId (fallback)
  const mirrorId = searchParams.get('mirrorId') || localStorage.getItem('mirrorCode') || localStorage.getItem('mirrorId');
  const salonId = searchParams.get('salonId') || localStorage.getItem('salonId');
  const qrToken = searchParams.get('qrToken') || localStorage.getItem('qrToken');
  
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const normalizeMobile = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    // Handle autofill like "+91 98xxxxxxx" or "9198xxxxxxx" by taking last 10 digits.
    if (digits.length <= 10) return digits;
    return digits.slice(-10);
  };

  useEffect(() => {
    pingHealth();
  }, []);

  useEffect(() => {
    // Persist QR params and resolve mirror -> Mongo IDs even if user lands directly here
    const run = async () => {
      if (mirrorId) localStorage.setItem('mirrorCode', mirrorId);
      if (salonId) localStorage.setItem('salonId', salonId);
      if (qrToken) localStorage.setItem('qrToken', qrToken);
      if (!mirrorId) return;
      try {
        const res = await resolveMirror(mirrorId);
        const mirror = res.data?.mirror;
        // Store internal immutable mirrorId for all API/session calls.
        if (mirror?.mirrorId) localStorage.setItem('mirrorId', mirror.mirrorId);
        if (mirror?.mirrorCode) localStorage.setItem('mirrorCode', mirror.mirrorCode);
        if (mirror?._id) localStorage.setItem('mirrorObjectId', mirror._id);
        if (mirror?.salon?._id) localStorage.setItem('salonId', mirror.salon._id);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve mirror from mobile-login:', e);
      }
    };
    run();
  }, [mirrorId, salonId, qrToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!mobile || mobile.length < 10) {
      setError('Please enter a valid mobile number');
      setLoading(false);
      return;
    }

    try {
      let internalMirrorId = localStorage.getItem('mirrorId') || undefined;
      const mirrorCodeFromQr = searchParams.get('mirrorId') || localStorage.getItem('mirrorCode');
      if (!internalMirrorId && mirrorCodeFromQr) {
        try {
          const res = await resolveMirror(mirrorCodeFromQr);
          internalMirrorId = res.data?.mirror?.mirrorId || undefined;
          if (internalMirrorId) localStorage.setItem('mirrorId', internalMirrorId);
          if (res.data?.mirror?.mirrorCode) {
            localStorage.setItem('mirrorCode', res.data.mirror.mirrorCode);
          }
        } catch (_) {
          // verify will resolve mirror aliases on the server
        }
      }

      const response = await sendOtp(mobile);
      const reqId = response.data?.reqId;
      if (!reqId) {
        throw new Error(response.data?.message || 'Failed to send OTP. Please try again.');
      }

      const tokenForOtp = qrToken || localStorage.getItem('qrToken') || undefined;
      navigate('/otp-verification', {
        state: {
          mobile,
          reqId,
          mirrorId: internalMirrorId || mirrorCodeFromQr,
          qrToken: tokenForOtp,
        },
      });
    } catch (error) {
      console.error('Error sending OTP:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        code: error.code,
        config: error.config
      });
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      const port = API_BASE_URL.match(/:(\d+)/)?.[1] || '3003';

      if (!error.response) {
        // Network error - server not reachable (wrong port, backend not running, CORS, etc.)
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          errorMessage = `Cannot connect to server. Ensure the backend is running and reachable at ${API_BASE_URL.replace(/\/api\/?$/, '')} (port ${port}).`;
        } else {
          errorMessage = `Network error: ${error.message}`;
        }
      } else if (error.response.status === 500) {
        errorMessage = error.response?.data?.message || 'Server error. Check that the backend and database (MongoDB) are running.';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="mobile-login-container">
      <div className="mobile-login-card">
        <div className="mobile-login-header">
          <BrandLogo variant="compact" className="mobile-login-logo" />
          <h1 className="mobile-login-title">Welcome</h1>
        </div>

        <p className="mobile-login-subtitle">
          Enter your mobile number to continue
        </p>

        {error && <div className="mobile-login-error">{error}</div>}

        <form onSubmit={handleSubmit} className="mobile-login-form">
          <div className="mobile-login-input-group">
            <label htmlFor="mobile">Mobile Number</label>
            <input
              id="mobile"
              type="tel"
              value={mobile}
              onChange={(e) => {
                setMobile(normalizeMobile(e.target.value));
                setError('');
              }}
              onInput={(e) => {
                // Some mobile browsers apply autofill after onChange; keep it normalized.
                const next = normalizeMobile(e.target.value);
                if (next !== mobile) setMobile(next);
              }}
              placeholder="Enter 10 digit mobile number"
              required
              autoComplete="tel"
              inputMode="numeric"
              pattern="[0-9]{10}"
            />
          </div>

          <button
            type="submit"
            className="mobile-login-btn"
            disabled={loading || mobile.length < 10}
          >
            {loading ? 'Sending OTP...' : 'Continue'}
          </button>
        </form>

        <AppVersion className="mobile-login-version" />

        {/* <div className="mobile-login-back">
          <button
            type="button"
            className="mobile-back-btn"
            onClick={() => navigate('/qr-login')}
          >
            ← Back
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default MobileLogin;
